import { supabase } from '../lib/supabase'
import { getCurrentUserId, verifyIssueAccess } from '../lib/auth'
import { createAuditLog } from '../lib/audit'
import type { MaintenanceVisit } from '../types/maintenance'

/**
 * Get all visits for an issue, ordered by most recent first.
 */
export async function getVisitsForIssue(issueId: string): Promise<MaintenanceVisit[]> {
  const userId = await getCurrentUserId()
  await verifyIssueAccess(issueId, userId)

  const { data, error } = await supabase
    .from('maintenance_visits')
    .select('*')
    .eq('issue_id', issueId)
    .order('proposed_at', { ascending: false })

  if (error) throw error
  return data as MaintenanceVisit[]
}

/**
 * Get the current active visit for an issue (proposed or accepted).
 */
export async function getActiveVisitForIssue(issueId: string): Promise<MaintenanceVisit | null> {
  const userId = await getCurrentUserId()
  await verifyIssueAccess(issueId, userId)

  const { data, error } = await supabase
    .from('maintenance_visits')
    .select('*')
    .eq('issue_id', issueId)
    .in('status', ['proposed', 'accepted'])
    .order('proposed_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error
  return data as MaintenanceVisit | null
}

/**
 * Auto-cancel all proposed or accepted visits for an issue.
 * Called when contractor is reassigned, issue is closed/cancelled, etc.
 */
export async function autoCancelVisits(
  issueId: string,
  reason: string
): Promise<void> {
  const { data: visits } = await supabase
    .from('maintenance_visits')
    .select('id, status')
    .eq('issue_id', issueId)
    .in('status', ['proposed', 'accepted'])

  if (!visits || visits.length === 0) return

  for (const visit of visits) {
    await supabase
      .from('maintenance_visits')
      .update({
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancel_reason: reason,
      })
      .eq('id', visit.id)

    createAuditLog({
      action: 'visit_cancelled',
      entityType: 'maintenance_visit',
      entityId: visit.id,
      metadata: { issue_id: issueId, reason, auto: true },
    }).catch(() => { /* Non-blocking */ })
  }

  // Remove calendar events for this issue
  try {
    await supabase.from('calendar_events')
      .delete()
      .eq('issue_id', issueId)
      .eq('event_type', 'maintenance_visit')
  } catch { /* Non-blocking */ }
}
